import { useState } from "react";
import { Star, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/components/auth-provider";
import { createReview, canReviewProperty } from "@/lib/review-service";
import { cn } from "@/lib/utils";

interface ReviewFormProps {
  propertyId: string;
  propertyTitle: string;
  landlordId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ReviewForm({
  propertyId,
  propertyTitle,
  landlordId,
  open,
  onOpenChange,
  onSuccess,
}: ReviewFormProps) {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCheckedEligibility, setHasCheckedEligibility] = useState(false);
  const [isEligible, setIsEligible] = useState(false);

  const checkEligibility = async () => {
    if (!user?.id) return;

    try {
      const result = await canReviewProperty(user.id, propertyId);
      setIsEligible(result.canReview);
      setHasCheckedEligibility(true);

      if (!result.canReview) {
        toast.error(
          result.reason || "You are not eligible to review this property"
        );
      }
    } catch (error) {
      console.error("Error checking eligibility:", error);
      toast.error("Failed to verify eligibility");
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Max size is 5MB.`);
        return false;
      }
      return true;
    });

    if (photos.length + validFiles.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    setPhotos((prev) => [...prev, ...validFiles]);
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!user?.id) {
      toast.error("You must be signed in to leave a review");
      return;
    }

    if (!hasCheckedEligibility) {
      await checkEligibility();
      return;
    }

    if (!isEligible) {
      toast.error("You are not eligible to review this property");
      return;
    }

    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!comment.trim()) {
      toast.error("Please write a review");
      return;
    }

    setIsSubmitting(true);

    try {
      await createReview({
        type: "property",
        propertyId,
        landlordId,
        reviewerId: user.id,
        reviewerName: user.name || "Anonymous",
        reviewerRole: "tenant",
        rating,
        title: `Review for ${propertyTitle}`,
        comment: comment.trim(),
        images: [], // Will be uploaded separately if needed
        verified: false, // Will be verified server-side
        status: "pending",
      });

      toast.success("Review submitted successfully!", {
        description:
          "Your review is pending moderation and will be visible soon.",
      });

      // Reset form
      setRating(0);
      setComment("");
      setPhotos([]);
      setHasCheckedEligibility(false);
      setIsEligible(false);

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to submit review"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Write a Review</DialogTitle>
          <DialogDescription>
            Share your experience with {propertyTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Rating */}
          <div>
            <Label>Rating *</Label>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    className={cn(
                      "h-8 w-8",
                      (hoverRating || rating) >= star
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    )}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <Label htmlFor="comment">Your Review *</Label>
            <Textarea
              id="comment"
              placeholder="Tell us about your experience living here..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={5}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {comment.length}/1000 characters
            </p>
          </div>

          {/* Photos */}
          <div>
            <Label>Photos (Optional)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Add up to 5 photos. Max 5MB each.
            </p>

            <div className="space-y-2">
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {photos.length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    document.getElementById("photo-input")?.click()
                  }
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Add Photos
                </Button>
              )}

              <input
                id="photo-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !rating || !comment.trim()}
          >
            {isSubmitting
              ? "Submitting..."
              : hasCheckedEligibility
              ? "Submit Review"
              : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
